import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { Box } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import DataFilter from "../../components/common/DataFilter";
import { PresetKey, presets } from "../../components/common/dataFilterPresets";
import AlertsMetrics from "../../components/dashboard/AlertsMetrics";
import AlertsResolutionsMetrics from "../../components/dashboard/AlertsResolutionsMetrics";
import AlertsResolutionsByAlertDateMetrics from "../../components/dashboard/AlertsResolutionsByAlertDateMetrics";
import SubscriptionResolutionsMetrics from "../../components/dashboard/SubscriptionResolutionsMetrics";
import AlertsActionsMetrics from "../../components/dashboard/AlertsActionsMetrics";
import { useCompanyId } from "../../hooks/useCompanyId";
import { Currency, CURRENCY_STORAGE_KEY } from "../Dashboard";

dayjs.extend(utc);

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useCompanyId();

  // Initialize state from URL params
  const initialPreset = (searchParams.get("range") as PresetKey) || "30D";
  const initialStart = searchParams.get("start")
    ? dayjs.utc(searchParams.get("start"))
    : presets[initialPreset].start();
  const initialEnd = searchParams.get("end")
    ? dayjs.utc(searchParams.get("end"))
    : presets[initialPreset].end();

  const [preset, setPreset] = useState<PresetKey>(initialPreset);
  const [startDate, setStartDate] = useState<Dayjs | null>(initialStart);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEnd);
  const [currency, setCurrency] = useState<Currency>(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    return (stored === "USD" || stored === "EUR") ? stored : "USD";
  });

  useEffect(() => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  }, [currency]);

  // Sync URL params (companyId is handled by useCompanyUrlSync in AppLayout)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("range", preset);
    if (startDate) params.set("start", startDate.format("YYYY-MM-DD"));
    if (endDate) params.set("end", endDate.format("YYYY-MM-DD"));
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, startDate, endDate]);

  // Handle preset change
  const handlePresetChange = (newPreset: PresetKey) => {
    setPreset(newPreset);
    if (newPreset !== "custom") {
      setStartDate(presets[newPreset].start());
      setEndDate(presets[newPreset].end());
    }
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    setPreset("custom");
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    setPreset("custom");
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Preventions Overview"
        description="Chargemont | Preventions Overview"
      />
      <PageContainer>
        <PageHeader title="Preventions Overview" />

        {/* Data Filter */}
        <Box sx={{ mb: 3 }}>
          <DataFilter
            preset={preset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={handlePresetChange}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            currency={currency}
            onCurrencyChange={setCurrency}
          />
        </Box>

        {/* Alerts Charts and Metrics */}
        <AlertsMetrics
          startDate={startDate?.format("YYYY-MM-DD") ?? ""}
          endDate={endDate?.format("YYYY-MM-DD") ?? ""}
          companyId={companyId}
          currency={currency}
          showTitle={false}
        />

        {/* Alerts Resolutions Charts (manual vs auto breakdown) */}
        <AlertsResolutionsMetrics
          startDate={startDate?.format("YYYY-MM-DD") ?? ""}
          endDate={endDate?.format("YYYY-MM-DD") ?? ""}
          companyId={companyId}
        />

        {/* Candidate replacement for the charts above: alert cm_status, bucketed on the alert's arrival date */}
        <AlertsResolutionsByAlertDateMetrics
          startDate={startDate?.format("YYYY-MM-DD") ?? ""}
          endDate={endDate?.format("YYYY-MM-DD") ?? ""}
          companyId={companyId}
        />

        {/* Alerts Actions Charts (full status breakdown) */}
        <AlertsActionsMetrics
          startDate={startDate?.format("YYYY-MM-DD") ?? ""}
          endDate={endDate?.format("YYYY-MM-DD") ?? ""}
          companyId={companyId}
        />

        {/* Subscription Resolutions Charts (manual vs auto subscription cancellations) */}
        <SubscriptionResolutionsMetrics
          startDate={startDate?.format("YYYY-MM-DD") ?? ""}
          endDate={endDate?.format("YYYY-MM-DD") ?? ""}
          companyId={companyId}
        />
      </PageContainer>
    </div>
  );
}
